import React from 'react'
import { Segment } from 'semantic-ui-react'
import {
  IColumn,
  PinnedStatus,
  RowHeight,
  onColumnMove,
  onColumnResize,
  onColumnResizeCommit,
  onDoubleClick,
  onFilterChange,
  onHoverPopup,
  onRowClassName,
  onRowHover
} from './Types'
import GridPanel from './GridPanel'
import useGridState, { UseGridStateProps } from './GridState'
import { getScrollbarSize } from './ScrollUtil'
import { RequestContext } from './Contexts/RequestContext'
import { Loader } from '@carsys/ui'

export interface IGridProps extends UseGridStateProps {
  /**
   * Attached property for the segment around the grid
   */
  attached?: boolean | 'top' | 'bottom'

  /**
   * Called when the cursor is hovering over a row
   *
   * @param row The row that is being hovered
   */
  onHover: onRowHover

  /**
   * Called when a column gets moved
   *
   * @param source The column that got moved
   * @param target The column or column index that the source is being moved to
   * @param pinnedStatus The target pin location
   */
  updateIndex: onColumnMove

  /**
   * Called when a column gets resized
   *
   * @param key The key of the column getting resized
   * @param width The new width of the column
   */
  resizeHeader: onColumnResize

  /**
   * Called when a column resize gets committed
   *
   * @param key The key of the column getting resized
   * @param width The width of the column
   */
  commitHeaderResize: onColumnResizeCommit

  /**
   * Called when the filter of a column got updated
   *
   * @param column The column that the filter is applied to
   * @param value The new values of the filter
   */
  onFilterChange: onFilterChange

  /**
   * Called when a row is double clicked
   *
   * @param row The row that got clicked on
   */
  onDoubleClick: onDoubleClick

  /**
   * Called and rendered in a tooltip when the row is hovered
   *
   * @param row The row that got hovered
   */
  onHoverPopup: onHoverPopup

  /**
   * Adds a CSS class to matching rows.
   */
  onRowClassName?: onRowClassName

  /**
   * Height for every row in the grid
   */
  rowHeight?: RowHeight

  /**
   * The grid tries to be 100% height, scaling height is not something that actually works in css, so every component that adds a bit of
   * height to the page should specify it in pixels so the grid can modify its height accordingly
   */
  heightOffset?: number

  /**
   * An error message to display instead of the grid
   */
  errorMessage?: JSX.Element

  /**
   * Prop used to hide uuid from dropdowns
   */
  hideUUIDColumns: boolean

  noDataPlaceholder?: React.ReactNode

  /**
   * A list of additional columns you want to display on top of the ones retrieved from the API
   */
  columns: IColumn[]

  /**
   * isFetching will render a linear loader at the bottom
   */
  isFetching?: boolean

  /**
   * Hides the column settings and reset columns button in the bottom left corner
   */
  hideColumnSettings?: boolean
}

export const rowHeights = {
  [RowHeight.Small]: 32,
  [RowHeight.Medium]: 42,
  [RowHeight.Large]: 52
}

export const Grid = (props: IGridProps) => {
  const {
    noDataPlaceholder,
    attached,
    errorMessage,
    isFetching,
    onDoubleClick,
    onHoverPopup,
    rowHeight = RowHeight.Medium,
    heightOffset = 0
  } = props

  const context = React.useContext(RequestContext)

  const headerRef = React.useRef<HTMLDivElement>(null)
  const bodyRef = React.useRef<HTMLDivElement>(null)
  const pinnedColumnsLeftRef = React.useRef<HTMLDivElement>(null)
  const pinnedColumnsRightRef = React.useRef<HTMLDivElement>(null)
  const containerRef = React.useRef<HTMLDivElement>(null)

  const { grid, updateIndex, commitHeaderResize, resizeHeader, onHover, onFilterChange } = useGridState(props)

  const [fillerWidth, setFillerWidth] = React.useState(0)

  let previousScrollTop = 0
  let previousScrollLeft = 0

  React.useEffect(() => {
    recalculateFillerWidth()
    updateContext()
  })

  React.useEffect(() => {
    window.addEventListener('resize', resizeEventListener)
    return () => window.removeEventListener('resize', resizeEventListener)
  }, [])

  const updateContext = () => {
    const newValue = grid?.data?.length > 0

    if (context.hasData === newValue) {
      return
    }
    context.setHasData(newValue)
  }

  const recalculateFillerWidth = () => {
    const container = containerRef.current
    if (!container) return

    const tableWidth = grid.headers.reduce((total, header) => total + header.width, 0) + getScrollbarSize()
    const shouldUseFiller = container.clientWidth > tableWidth
    const newFillerWidth = shouldUseFiller ? container.clientWidth - tableWidth - 1 : 0

    if (fillerWidth === newFillerWidth) {
      return
    }
    setFillerWidth(newFillerWidth)
  }

  const resizeEventListener = () => {
    recalculateFillerWidth()
  }

  const handleScrollLeft = (event: React.SyntheticEvent<HTMLDivElement>) => {
    const target = event.currentTarget

    if (event.target !== target) {
      return
    }

    const scrollLeft = target.scrollLeft

    const header = headerRef.current
    const body = bodyRef.current

    if (!body || !header) return

    if (scrollLeft !== previousScrollLeft) {
      if (target && target === header) {
        body.scrollLeft = scrollLeft
      }

      if (target && target === body) {
        header.scrollLeft = scrollLeft
      }
    }

    previousScrollLeft = scrollLeft
  }

  const handleScrollTop = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget

    if (event.target !== target) return
    if (event.target === headerRef.current) return

    const scrollTop = target.scrollTop

    const body = bodyRef.current
    const pinnedColumnsLeft = pinnedColumnsLeftRef.current
    const pinnedColumnsRight = pinnedColumnsRightRef.current

    if (scrollTop !== previousScrollTop) {
      if (body && target !== body) {
        body.scrollTop = scrollTop
      }

      if (pinnedColumnsLeft && target !== pinnedColumnsLeft) {
        pinnedColumnsLeft.scrollTop = scrollTop
      }

      if (pinnedColumnsRight && target !== pinnedColumnsRight) {
        pinnedColumnsRight.scrollTop = scrollTop
      }
    }

    previousScrollTop = scrollTop
  }

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    handleScrollLeft(event)
    handleScrollTop(event)
  }

  return (
    <Segment attached={attached}>
      <div className='cbgms-grid cbgms-grid-fixed-header cbgms-grid-scroll-position-middle' ref={containerRef}>
        <div className='cbgms-grid-content'>
          <div className='cbgms-grid-scroll'>
            <GridPanel
              noDataPlaceholder={noDataPlaceholder}
              data-testid='cbgms-grid__center-panel'
              headerContent={grid.headers}
              commitHeaderResize={commitHeaderResize}
              resizeHeader={resizeHeader}
              onHeaderMove={updateIndex}
              onFilterChange={onFilterChange}
              content={grid.data}
              filter={PinnedStatus.Center}
              onHover={onHover}
              onDoubleClick={onDoubleClick}
              onRowClassName={props.onRowClassName}
              onHoverPopup={onHoverPopup}
              hovered={grid.hovered}
              headerRef={headerRef}
              bodyRef={bodyRef}
              onScroll={handleScroll}
              rowHeight={rowHeights[rowHeight]}
              heightOffset={heightOffset}
              fillerWidth={fillerWidth}
              errorMessage={errorMessage}
            />
          </div>
          {grid.headers.filter(header => header.pinned === PinnedStatus.Left).length > 0 && (
            <div className='cbgms-grid-fixed-left'>
              <GridPanel
                data-testid='cbgms-grid__left-panel'
                headerContent={grid.headers.filter(h => h.pinned === PinnedStatus.Left)}
                commitHeaderResize={commitHeaderResize}
                resizeHeader={resizeHeader}
                onHeaderMove={updateIndex}
                onFilterChange={onFilterChange}
                content={grid.data}
                filter={PinnedStatus.Left}
                onHover={onHover}
                onDoubleClick={onDoubleClick}
                onRowClassName={props.onRowClassName}
                onHoverPopup={onHoverPopup}
                hovered={grid.hovered}
                bodyRef={pinnedColumnsLeftRef}
                onScroll={handleScrollTop}
                rowHeight={rowHeights[rowHeight]}
                heightOffset={heightOffset}
                fillerWidth={fillerWidth}
                errorMessage={errorMessage}
              />
            </div>
          )}
          {grid.headers.filter(h => h.pinned === PinnedStatus.Right).length > 0 && (
            <div className='cbgms-grid-fixed-right' style={{ zIndex: 2 }}>
              <GridPanel
                data-testid='cbgms-grid__right-panel'
                headerContent={grid.headers.filter(h => h.pinned === PinnedStatus.Right)}
                commitHeaderResize={commitHeaderResize}
                resizeHeader={resizeHeader}
                onHeaderMove={updateIndex}
                onFilterChange={onFilterChange}
                content={grid.data}
                filter={PinnedStatus.Right}
                onHover={onHover}
                onDoubleClick={onDoubleClick}
                onRowClassName={props.onRowClassName}
                hovered={grid.hovered}
                bodyRef={pinnedColumnsRightRef}
                onScroll={handleScrollTop}
                rowHeight={rowHeights[rowHeight]}
                heightOffset={heightOffset}
                fillerWidth={fillerWidth}
                errorMessage={errorMessage}
              />
            </div>
          )}
        </div>
      </div>
      <div style={{ width: '100%', marginTop: '-4px' }}>
        {isFetching ? <Loader linear loading={isFetching} /> : <div style={{ width: '100%', marginBottom: '4px' }} />}
      </div>
    </Segment>
  )
}

export default Grid
